import { HttpResponse } from '@angular/common/http';
import { Injectable, Type } from '@angular/core';
import {
  ApiPaginatedResponse,
  ApiQuery,
  BaseApiService,
  paginatedFromArray,
} from '@kinet-open-network/client/core/api-client';
import { mapToModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import {
  createEnumList,
  ICustomer,
  IGroup,
  IStockMove,
  IStockMoveActionData,
  IUser,
  MainStockType,
  mainStockTypeOptions,
  StockType,
} from '@kinet-open-network/shared/data-access/model';
import { map, Observable, of, tap } from 'rxjs';
import { StockMove } from '../models/stock-move.model';

@Injectable({
  providedIn: 'root',
})
export class StockMovesService extends BaseApiService<StockMove> {
  override model: Type<StockMove> = StockMove;

  override get endpoint(): string {
    return '/stock-moves';
  }

  exportMoveItems(moveId: string): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.endpoint}/${moveId}/export-items`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const blob = response.body as Blob;
          const filename = getFilenameFromHeaders(response.headers);
          downloadBlob(
            blob,
            filename ??
              `export-${StockMove.modelName.toLowerCase()}s-${new Date().getTime()}.csv`,
          );
        }),
      );
  }

  exportToAccounting(moveId: string) {
    return this.http
      .post<IStockMove>(`${this.endpoint}/${moveId}/export-to-accounting`, {})
      .pipe(mapToModel(StockMove));
  }

  importFromAccounting(from?: Date | null): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/import-from-accounting`, {
      from,
    });
  }

  // queryStockOptions = (
  //   customersService: {
  //     query: (q?: ApiQuery | null) => Observable<ApiPaginatedResponse<any>>;
  //   },
  //   groupsService: {
  //     query: (q?: ApiQuery | null) => Observable<ApiPaginatedResponse<any>>;
  //   },
  //   usersService: {
  //     query: (q?: ApiQuery | null) => Observable<ApiPaginatedResponse<any>>;
  //   },
  //   type?: StockType,
  //   q?: ApiQuery | null,
  // ): Observable<ApiPaginatedResponse<any>> => {
  //   const mapResponse = <T extends { id: string | number; name?: string }>(
  //     response: ApiPaginatedResponse<T>,
  //     mapLabel: (i: T) => string = (i) => i.name ?? i.id.toString(),
  //     mapId: (i: T) => string = (i) => i.id.toString(),
  //   ) => ({
  //     ...response,
  //     data: response.data?.map((i) => ({
  //       label: mapLabel(i),
  //       id: mapId(i),
  //     })),
  //   });

  //   const plainResponse = <T>(data: T[]) => of(paginatedFromArray(data));

  //   if (type === StockType.customer) {
  //     return customersService
  //       .query(q)
  //       .pipe(map((data) => mapResponse(data, (i) => i.fullname || '--')));
  //   } else if (type === StockType.group) {
  //     return groupsService.query(q).pipe(map((data) => mapResponse(data)));
  //   } else if (type === StockType.user) {
  //     return usersService
  //       .query(q)
  //       .pipe(map((data) => mapResponse(data, (i) => i.fullName || i.name)));
  //   } else if (type === StockType.stock) {
  //     return plainResponse(
  //       createEnumList(mainStockTypeOptions, true).map((item) => ({
  //         id: item.value,
  //         label: item.label,
  //       })),
  //     );
  //   }

  //   return plainResponse([]);
  // };

  queryStockOptions(
    stockType?: StockType,
    q?: ApiQuery | null,
  ): Observable<ApiPaginatedResponse<{ label: string; id: string }>> {
    const mapResponse = <
      T extends { id: string | number; name?: string; deletedAt?: Date | null },
    >(
      response: ApiPaginatedResponse<T>,
      mapLabel: (i: T) => string = (i) => i.name ?? i.id.toString(),
      mapId: (i: T) => string = (i) => i.id.toString(),
    ) => ({
      ...response,
      data: response.data?.map((i) => ({
        label: mapLabel(i),
        id: mapId(i),
        deletedAt: i.deletedAt,
      })),
    });

    const plainResponse = <T>(data: T[]) => of(paginatedFromArray(data));

    if (stockType === StockType.customer) {
      return this.http
        .get<ApiPaginatedResponse<ICustomer>>('/customers', {
          params: this.apiQueryToHttpParams({
            ...q,
            fields: Array.from(
              new Set([...(q?.fields || []), 'id', 'fullname', 'deletedAt']),
            ),
            deleted: true,
          }),
        })
        .pipe(
          map((data) =>
            mapResponse(data, (i) => `${i.fullname || '--'} (${i.id})`),
          ),
        );
    } else if (stockType === StockType.group) {
      return this.http
        .get<ApiPaginatedResponse<IGroup>>('/groups', {
          params: this.apiQueryToHttpParams({
            ...q,
            fields: Array.from(new Set([...(q?.fields || []), 'id', 'name'])),
          }),
        })
        .pipe(map((data) => mapResponse(data)));
    } else if (stockType === StockType.user) {
      return this.http
        .get<ApiPaginatedResponse<IUser>>('/users', {
          params: this.apiQueryToHttpParams({
            ...q,
            fields: Array.from(
              new Set([...(q?.fields || []), 'id', 'fullName', 'name']),
            ),
          }),
        })
        .pipe(
          map((data) => mapResponse(data, (i) => `${i.fullName || i.name}`)),
        );
    } else if (stockType === StockType.stock) {
      return plainResponse(
        createEnumList<MainStockType>(mainStockTypeOptions, true).map(
          (item) => ({
            id: item.value,
            label: item.label,
          }),
        ),
      );
    }

    return plainResponse([]);
  }

  batchAction(data: IStockMoveActionData, query?: ApiQuery | null) {
    return this.http.post(`${this.endpoint}/batch-action`, data, {
      params: this.apiQueryToHttpParams(query),
    });
  }
}
